// usePdfCanvasViewer.js
// Pan/zoom-canvas-engine voor PDF's: eigen translate/scale-transform (direct
// op de DOM, buiten React's render cycle) voor 60fps pan/zoom, resolutie-
// bewust her-renderen bij inzoomen, een pan-clamp die een as bevriest zodra
// de inhoud al past, en Page als default fit.

import { useState, useRef, useEffect, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { CASE_PREFETCH, getPrefetchedPdf, beginForeground, storePdf } from "../../utils/casePrefetch";
import { PDF_EDGE_BOUNCE } from "../../featureFlags";

// public/pdf.worker.min.js is een kopie van node_modules/pdfjs-dist/build/pdf.worker.min.mjs
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const MIN_SCALE = 0.05;
const MAX_SCALE = 8;
const MAX_RENDER_SCALE = 4;      // cap op fysieke canvas-resolutie (geheugen/perf)
// Op een gewoon (niet-Retina) scherm (devicePixelRatio 1) toch minstens op 2x
// tekenen en de browser laten verkleinen: kleine tekst wordt dan gladder en
// leesbaarder (vooral Safari tekent canvas-tekst op 1x grof). Kost op zo'n
// scherm 4x canvas-geheugen. Op 1 = oude gedrag.
const MIN_RENDER_DPR = 2;
// Safari toont een canvas binnen de geschaalde wereld (zoom van de viewer)
// altijd opnieuw bemonsterd en dus zacht: een 2x-canvas op een 1x-scherm
// wordt wazig verkleind, en zelfs een 1x-canvas wordt nog eens herschaald.
// Daarom in Safari zelf van de 2x-render naar precies de schermresolutie
// verkleinen en de canvas tegen-schalen (scale(1/wereldschaal)), zodat hij
// netto 1-op-1 op schermpixels staat. Alleen als we boven de scherm-dpr
// tekenen (1x-schermen); Retina en Chrome blijven zoals ze waren.
// Op false = oude gedrag.
const SAFARI_PIXEL_EXACT = true;
// Bij zoomen met knop/toets/dubbelklik (animatie met bekende eindstand) de
// scherpe versie al tijdens de animatie tekenen en daarna niet nog 160ms
// wachten. Op false = oude gedrag (pas tekenen 160ms na de animatie).
const FAST_SHARPEN = true;
const IS_SAFARI = typeof navigator !== "undefined"
  && /^((?!chrome|chromium|crios|fxios|edg|android).)*safari/i.test(navigator.userAgent);
const renderDpr = () => Math.min(Math.max(window.devicePixelRatio || 1, MIN_RENDER_DPR), 2.5);
// Factor van de render-resolutie naar de uiteindelijke canvas (1 = zelfde)
const pixelExactFactor = () => {
  if (!SAFARI_PIXEL_EXACT || !IS_SAFARI) return 1;
  const k = (window.devicePixelRatio || 1) / renderDpr();
  return k < 0.99 ? k : 1;
};
const PAGE_GAP = 32;             // wereld-units tussen pagina's
const PAGE_RADIUS = 8;           // schermpixels hoekradius per pagina
const PAD = 24;                  // schermpixels marge voor fit-berekeningen/top-anker
// Onzichtbare tekstlaag (pdf.js TextLayer) over elke pagina, zodat tekst te
// selecteren/kopiëren is (en Ctrl/Cmd+F werkt). Dubbelklik selecteert dan een
// woord i.p.v. in te zoomen. Op false = oude gedrag (alleen canvas, dubbelklik
// zoomt). Op een touchscherm nooit: daar is selecteren niet nodig en de
// duizenden spans maakten zoomen/pannen op de iPhone traag.
const IS_TOUCH_SCREEN = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
export const ENABLE_TEXT_LAYER = !IS_TOUCH_SCREEN;
// Touchscherm: scrollen (één vinger) doet de browser zelf, met z'n eigen
// vaart en afremming (loopt ook door als de main thread bezig is met
// tekenen). De viewport is dan een echte scroll-container; pinch en dubbeltik
// blijven van ons. Desktop: alles via de transform, zoals altijd.
export const NATIVE_TOUCH_SCROLL = IS_TOUCH_SCREEN;
// Bij een nieuw document pas infaden als de eerste pagina's echt getekend
// zijn. De <canvas>-elementen worden hergebruikt, dus tot dan staat daar nog
// het vorige document in (gaf een flits van de oude pdf bij case-wissel).
// Op false = oude gedrag (infaden zodra het tekenen is aangevraagd).
const WAIT_FOR_FIRST_DRAW = true;
const FIRST_DRAW_TIMEOUT_MS = 1500;
// Een nieuwe render van een pagina breekt een nog lopende render van diezelfde
// pagina niet meer af, maar wacht erop. Afbreken-en-direct-opnieuw liet pdf.js
// sporadisch onvolledig tekenen (halve letters / missende tekst in plaatjes,
// "Dependent image isn't ready yet"). Op true = oude gedrag (afbreken).
const CANCEL_RUNNING_RENDER = false;
// Verder ingezoomd dan de pagina in de viewport past: slepen met links (niet
// vanaf tekst) pant, net als bij de afbeeldingen in de drawer. Dubbelklik
// naast tekst zet terug naar Fit page. Op false = oude gedrag (pannen alleen
// met spatie/middelste muisknop, dubbelklik doet niets bij een tekstlaag).
const DRAG_PAN_WHEN_ZOOMED = true;
const DOUBLE_CLICK_FIT_PAGE = true;

function clamp(v, min, max) { return Math.min(Math.max(v, min), max); }

// PDF_EDGE_BOUNCE: rubberband zoals iOS. De inhoud schiet met de snelheid
// waarmee hij de rand raakt nog iets door, remt af en veert terug via een
// kritisch gedempte veer (geen na-stuiteren): x(t) = v0 · t · e^(-ω·t).
const EDGE_SPRING_OMEGA = 0.011;   // 1/ms; hoger = strakker/korter
const EDGE_RUBBER_PX = 40;         // weerstand: verder doorschieten wordt steeds zwaarder
const EDGE_NUDGE_SPEED = 0.45;     // px/ms als je al op de rand staat en nog eens drukt
// Scrollen naar de rand eindigt mét snelheid (helling 0.4 i.p.v. 0), zodat
// het doorschieten daar naadloos op aansluit
const easeIntoEdge = (t) => t * (1.6 - 0.6 * t);
// Rubberband-weerstand (formule van iOS): hoe verder voorbij de rand, hoe
// minder de inhoud nog meegaat. dim = viewport-maat in die richting.
function rubber(x, dim) {
  if (!x || !dim) return 0;
  return Math.sign(x) * (1 - 1 / ((Math.abs(x) * 0.55) / dim + 1)) * dim;
}
// Veer-curve per as als keyframes voor de browser. Per as { x0, v0 } in
// translate-px (en px/ms): x(t) = (x0 + (v0 + ω·x0)·t)·e^(-ω·t), kritisch
// gedempt. Vanaf 0 met snelheid (doorschieten) gaat er nog de rubberband-
// weerstand overheen; vanaf een uitwijking (loslaten na slepen) niet, die
// is al geremd.
function edgeSpringKeyframes(ax, ay) {
  const w = EDGE_SPRING_OMEGA;
  const at = (a, t) => {
    if (!a) return 0;
    const raw = (a.x0 + (a.v0 + w * a.x0) * t) * Math.exp(-w * t);
    return a.x0 ? raw : Math.sign(raw) * EDGE_RUBBER_PX * (1 - 1 / (Math.abs(raw) / EDGE_RUBBER_PX + 1));
  };
  const pts = [];
  const step = 1000 / 120;
  for (let t = 0; ; t += step) {
    const x = at(ax, t);
    const y = at(ay, t);
    pts.push({ t, x, y });
    if (t > 1 / w && Math.abs(x) < 0.2 && Math.abs(y) < 0.2) break;
  }
  const total = pts[pts.length - 1].t;
  Object.assign(pts[pts.length - 1], { x: 0, y: 0 });
  return {
    duration: total,
    keyframes: pts.map((q) => ({ translate: `${q.x.toFixed(2)}px ${q.y.toFixed(2)}px`, offset: q.t / total })),
  };
}

// wereldpunt (wx,wy) moet op schermpunt (sx,sy) landen bij de gegeven scale
function focusOn(wx, wy, sx, sy, scale) {
  return { x: sx - wx * scale, y: sy - wy * scale };
}

/**
 * @param {object} opts
 * @param {string|null} opts.url - URL naar de PDF (onze eigen /files/proxy-route,
 *   niet rechtstreeks een GCS signed url — zie de route zelf voor waarom)
 * @param {string|number|null} opts.cacheKey - identificeert WELK document dit is
 *   (bv. `orderform-${rowData.id}` of `misc-${file.id}`); bij wijziging wordt
 *   het document opnieuw geladen, ook als de url toevallig hetzelfde is.
 * @param {"width"|"page"|null} opts.defaultFit - fit-modus direct na laden;
 *   null betekent "geen fit-modus, gebruik opts.initialScale" (een eerder
 *   handmatig gekozen zoomniveau dat geen Width/Page fit is)
 * @param {number|null} opts.initialScale - alleen gebruikt als defaultFit
 *   null is: het scale-niveau waarop direct na laden ingezoomd wordt
 */
// arrowKeys=false: ↑/↓ niet gebruiken voor scrollen (bv. bij bijlagen, waar
// de drawer ze gebruikt om door de bestanden te bladeren)
// active=false: het document wel alvast laden (url gezet), maar nog niet
// tonen/tekenen — status blijft dan "idle" tot active weer true is.
export function usePdfCanvasViewer({ url, cacheKey, defaultFit = "page", initialScale = null, arrowKeys = true, active = true }) {
  // Laadstatus van het document zelf; 'status' hieronder is wat de viewer
  // ziet (alleen "ready" als 'ie ook actief is)
  const [loadState, setLoadState] = useState("idle"); // idle | loading | loaded | error
  const status = !active ? "idle" : loadState === "loaded" ? "ready" : loadState; // idle | loading | ready | error
  const [errorMsg, setErrorMsg] = useState("");
  // Ophogen = opnieuw laden (na een fout, zie hieronder)
  const [retryToken, setRetryToken] = useState(0);
  const [ui, setUi] = useState({ scale: 1, pageIndex: 0, numPages: 0, fitMode: null });
  // Apart van 'status': pas true zodra de eerste fit ook echt is toegepast,
  // zodat de UI de inhoud pas toont (fade-in) als 'ie al goed gepositioneerd
  // staat — voorkomt een zichtbare "spring" naar de juiste plek.
  const [contentReady, setContentReady] = useState(false);

  const pdfDocRef = useRef(null);
  const pagesMetaRef = useRef({ pages: null, maxWidth: 0 });
  const renderedScaleRef = useRef([]);
  const renderTasksRef = useRef({});
  // Per pagina een volgnummer van de laatst aangevraagde render: een verzoek
  // dat tijdens het wachten is ingehaald door een nieuwer verzoek vervalt
  const renderReqRef = useRef({});
  const canvasElsRef = useRef({});
  const worldElRef = useRef(null);
  const viewportElRef = useRef(null);
  const transformRef = useRef({ x: 0, y: 0, scale: 1 });
  const rafRef = useRef(null);
  const settleTimerRef = useRef(null);
  const spaceHeldRef = useRef(false);
  const dragRef = useRef(null);
  const momentumRafRef = useRef(null);
  // lopende animateTo-animatie + het doel ervan (pijltjes-scroll bouwt bij
  // ingedrukt houden voort op het doel i.p.v. op de tussenpositie)
  const animRafRef = useRef(null);
  const animTargetRef = useRef(null);
  const bounceAnimRef = useRef(null);
  const fitModeRef = useRef(null);
  const loadedForKeyRef = useRef(null);
  const loadedForUrlRef = useRef(null);
  // Tekstlaag per pagina: het DOM-element (via registerTextLayer) en de
  // lopende TextLayer. docTokenRef telt per geladen document op; een element
  // dat met een ander token getekend is (React hergebruikt de pagina-divs bij
  // een nieuw document met evenveel pagina's) wordt opnieuw gevuld.
  const textLayerElsRef = useRef({});
  const textLayerTasksRef = useRef({});
  const docTokenRef = useRef(0);
  // Promises van de laatst aangevraagde canvas-renders (zie upgradeVisiblePages)
  const pendingDrawsRef = useRef([]);

  const setFitMode = useCallback((mode) => {
    fitModeRef.current = mode;
    setUi((u) => ({ ...u, fitMode: mode }));
  }, []);

  // NATIVE_TOUCH_SCROLL: waar de world binnen de scroll-inhoud staat
  // (x = ox - scrollLeft) en het element dat de scroll-inhoud groot maakt
  const nativeOriginRef = useRef({ ox: 0, oy: 0 });
  const sizerElRef = useRef(null);

  const applyTransform = useCallback(() => {
    const { x, y, scale } = transformRef.current;
    if (!worldElRef.current) return;
    if (NATIVE_TOUCH_SCROLL) {
      const vp = viewportElRef.current;
      const meta = pagesMetaRef.current;
      if (vp && meta.pages && meta.pages.length) {
        // Past de inhoud in een as: geen scroll, de world staat op x/y.
        // Anders: world op PAD en de positie via scrollLeft/Top.
        const last = meta.pages[meta.pages.length - 1];
        const cw = meta.maxWidth * scale, ch = (last.top + last.height) * scale;
        const vw = vp.clientWidth, vh = vp.clientHeight;
        const fitsX = cw + 2 * PAD <= vw, fitsY = ch + 2 * PAD <= vh;
        const ox = fitsX ? x : PAD, oy = fitsY ? y : PAD;
        nativeOriginRef.current = { ox, oy };
        if (worldElRef.current.style.willChange !== "transform") worldElRef.current.style.willChange = "transform";
        worldElRef.current.style.transform = `translate(${ox}px, ${oy}px) scale(${scale})`;
        worldElRef.current.style.setProperty("--pdf-page-radius", `${PAGE_RADIUS / scale}px`);
        if (sizerElRef.current) {
          sizerElRef.current.style.width = `${fitsX ? 1 : cw + 2 * PAD}px`;
          sizerElRef.current.style.height = `${fitsY ? 1 : ch + 2 * PAD}px`;
        }
        vp.scrollLeft = fitsX ? 0 : ox - x;
        vp.scrollTop = fitsY ? 0 : oy - y;
        // Werkelijke (door de browser geklemde) positie
        transformRef.current = { x: ox - vp.scrollLeft, y: oy - vp.scrollTop, scale };
        return;
      }
    }
    // will-change alleen tijdens bewegen: met will-change tekent de browser de
    // laag niet opnieuw op de nieuwe zoom, en blijft alles na het zoomen zacht
    // (zie scheduleSettle, waar 'ie er weer af gaat)
    if (worldElRef.current.style.willChange !== "transform") worldElRef.current.style.willChange = "transform";
    worldElRef.current.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    // Hoekradius van de pagina's in wereld-units, zodat die op het scherm
    // altijd PAGE_RADIUS px is, ongeacht het zoomniveau
    worldElRef.current.style.setProperty("--pdf-page-radius", `${PAGE_RADIUS / scale}px`);
  }, []);

  const queueApply = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => { applyTransform(); rafRef.current = null; });
  }, [applyTransform]);

  // bevriest een as zodra de inhoud al in de viewport past (niks om naar toe
  // te scrollen); anders geklemd tegen de randen van de content, met PAD marge
  function clampPan(x, y, scale) {
    const vp = viewportElRef.current;
    const meta = pagesMetaRef.current;
    const cur = transformRef.current;
    if (!vp || !meta.pages || !meta.pages.length) return { x: cur.x, y: cur.y };
    const contentW = meta.maxWidth * scale;
    const last = meta.pages[meta.pages.length - 1];
    const contentH = (last.top + last.height) * scale;
    const vw = vp.clientWidth, vh = vp.clientHeight;
    const xMin = vw - contentW - PAD, xMax = PAD;
    const yMin = vh - contentH - PAD, yMax = PAD;
    return {
      x: xMin > xMax ? cur.x : clamp(x, xMin, xMax),
      y: yMin > yMax ? cur.y : clamp(y, yMin, yMax),
    };
  }

  // Welke assen echt te scrollen zijn (inhoud groter dan de viewport). Voor
  // de rubberband: verticaal altijd (zoals iOS), horizontaal alleen als er
  // horizontaal iets te scrollen valt, anders wiebelt 'ie zijwaarts mee.
  function rubberAxes(scale) {
    const vp = viewportElRef.current;
    const meta = pagesMetaRef.current;
    if (!vp || !meta.pages || !meta.pages.length) return { x: false, y: true };
    return { x: meta.maxWidth * scale + 2 * PAD > vp.clientWidth, y: true };
  }

  // Na een zoom: past de inhoud in een as, dan in die as centreren; past hij niet, dan
  // klemmen tegen de randen zodat er geen lege ruimte ontstaat. Zonder dit
  // blijft de content na uitzoomen rond het oude beeldcentrum "hangen".
  const constrainZoom = useCallback((x, y, scale) => {
    const vp = viewportElRef.current;
    const meta = pagesMetaRef.current;
    if (!vp || !meta.pages || !meta.pages.length) return { x, y };
    const contentW = meta.maxWidth * scale;
    const last = meta.pages[meta.pages.length - 1];
    const contentH = (last.top + last.height) * scale;
    const vw = vp.clientWidth, vh = vp.clientHeight;
    const xMin = vw - contentW - PAD, xMax = PAD;
    const yMin = vh - contentH - PAD, yMax = PAD;
    return {
      x: xMin > xMax ? (vw - contentW) / 2 : clamp(x, xMin, xMax),
      y: yMin > yMax ? (vh - contentH) / 2 : clamp(y, yMin, yMax),
    };
  }, []);

  const computeCurrentPage = useCallback(() => {
    const vp = viewportElRef.current;
    const meta = pagesMetaRef.current;
    if (!vp || !meta.pages) return 0;
    const { y, scale } = transformRef.current;
    const centerY = (vp.clientHeight / 2 - y) / scale;
    for (let i = 0; i < meta.pages.length; i++) {
      const p = meta.pages[i];
      if (centerY >= p.top && centerY <= p.top + p.height) return i;
    }
    let nearest = 0, best = Infinity;
    meta.pages.forEach((p, i) => {
      const mid = p.top + p.height / 2;
      const d = Math.abs(mid - centerY);
      if (d < best) { best = d; nearest = i; }
    });
    return nearest;
  }, []);

  // Schuift elke canvas een fractie van een pixel binnen z'n pagina, zodat
  // de pixels van de canvas precies op die van het scherm vallen. Anders
  // staat elke pixel half tussen twee schermpixels in en wordt tekst zacht.
  const alignCanvases = useCallback(() => {
    const meta = pagesMetaRef.current;
    if (!meta.pages) return;
    const { x, y, scale } = transformRef.current;
    const dpr = window.devicePixelRatio || 1;
    meta.pages.forEach((p, i) => {
      const canvas = canvasElsRef.current[i];
      if (!canvas) return;
      const sx = (x + p.left * scale) * dpr, sy = (y + p.top * scale) * dpr;
      const fx = sx - Math.round(sx), fy = sy - Math.round(sy);
      // dataset.cs: zie SAFARI_PIXEL_EXACT (eerst verschuiven in wereld-units,
      // dan tegen-schalen)
      canvas.style.transform = `translate(${-fx / (dpr * scale)}px, ${-fy / (dpr * scale)}px)`
        + (canvas.dataset.cs ? ` scale(${1 / Number(canvas.dataset.cs)})` : "");
    });
  }, []);

  const renderPageToCanvas = useCallback(async (index, renderScale) => {
    const doc = pdfDocRef.current;
    const canvas = canvasElsRef.current[index];
    const token = String(docTokenRef.current);
    if (!doc || !canvas) return;
    // Intussen een ander document geladen: dit niet meer tekenen
    const isCurrent = () => pdfDocRef.current === doc && String(docTokenRef.current) === token;
    let off = null;
    try {
      const running = renderTasksRef.current[index];
      if (CANCEL_RUNNING_RENDER) {
        if (running) { try { running.cancel(); } catch (e) {} }
      } else {
        const req = (renderReqRef.current[index] || 0) + 1;
        renderReqRef.current[index] = req;
        if (running) {
          await running.promise.catch(() => {});
          // Intussen een nieuwer verzoek voor deze pagina: dat tekent wel
          if (renderReqRef.current[index] !== req || !isCurrent()) return;
        }
      }
      const page = await doc.getPage(index + 1);
      if (!isCurrent()) return;
      const viewport = page.getViewport({ scale: renderScale });
      // Eerst buiten beeld tekenen en pas daarna in één keer overzetten: de
      // canvas zelf van maat veranderen maakt 'm leeg, en dan knippert de
      // pagina wit zolang pdf.js nog bezig is
      off = document.createElement("canvas");
      off.width = Math.ceil(viewport.width);
      off.height = Math.ceil(viewport.height);
      const task = page.render({ canvasContext: off.getContext("2d"), viewport });
      renderTasksRef.current[index] = task;
      await task.promise;
      if (renderTasksRef.current[index] !== task || !isCurrent()) return;
      const target = canvasElsRef.current[index];
      if (!target) return;
      const k = pixelExactFactor();
      if (k < 1) {
        // SAFARI_PIXEL_EXACT: zelf verkleinen naar schermresolutie ("low" =
        // bilineair; bij 2:1 precies het gemiddelde, scherper dan "high")
        const devScale = renderScale * k;
        const dpr = window.devicePixelRatio || 1;
        target.width = Math.max(1, Math.round(off.width * k));
        target.height = Math.max(1, Math.round(off.height * k));
        const tctx = target.getContext("2d");
        tctx.imageSmoothingEnabled = true;
        tctx.imageSmoothingQuality = "low";
        tctx.drawImage(off, 0, 0, target.width, target.height);
        // Css-maat in schermpixels; alignCanvases schaalt terug met 1/cs, zodat
        // de canvas in wereld-units even groot is als zonder deze stap
        target.dataset.cs = String(devScale / dpr);
        target.style.transformOrigin = "0 0";
        target.style.width = `${target.width / dpr}px`;
        target.style.height = `${target.height / dpr}px`;
      } else {
        target.width = off.width;
        target.height = off.height;
        target.getContext("2d").drawImage(off, 0, 0);
        delete target.dataset.cs;
        target.style.transformOrigin = "";
        // Precies de getekende maat (in wereld-units) i.p.v. 100% van de
        // pagina: door het afronden naar hele pixels scheelt dat een fractie,
        // en dan zou de hele canvas nét geschaald (en dus wazig) getoond worden
        target.style.width = `${off.width / renderScale}px`;
        target.style.height = `${off.height / renderScale}px`;
      }
      // Van welk document deze canvas nu een plaatje bevat (zie clearStaleCanvases)
      target.dataset.pdfDoc = token;
      alignCanvases();
    } catch (e) {
      if (e?.name !== "RenderingCancelledException") {
        console.error(e);
        // Mislukt: niet als "getekend" laten staan, zodat de volgende
        // pan/zoom-settle het opnieuw probeert i.p.v. een half plaatje te houden
        if (isCurrent() && renderedScaleRef.current) renderedScaleRef.current[index] = 0;
      }
    } finally {
      // Hulp-canvas meteen vrijgeven (ook na afbreken/fout) i.p.v. te wachten
      // op de garbage collector: browsers hebben een plafond voor canvas-
      // geheugen, en elke zoomstap maakt een nieuwe op volle resolutie.
      // Pas ná de volgende frame: Chrome voert de drawImage hierboven (GPU)
      // soms pas later uit, en een al geleegde bron gaf dan halve letters.
      if (off) {
        const o = off;
        requestAnimationFrame(() => setTimeout(() => { o.width = 0; o.height = 0; }, 0));
      }
    }
  }, [alignCanvases]);

  // De <canvas>- en tekstlaag-elementen worden hergebruikt voor een volgend
  // document (bv. de volgende case). Zolang ze niet opnieuw getekend zijn
  // bevatten ze nog het VORIGE document — dat mag nooit zichtbaar worden of
  // via zoeken/kopiëren (Ctrl+F, Cmd+A) vindbaar blijven.
  const clearCanvas = (canvas) => {
    canvas.width = 0;
    canvas.height = 0;
    delete canvas.dataset.pdfDoc;
    delete canvas.dataset.cs;
  };
  const clearStaleCanvases = useCallback(() => {
    const token = String(docTokenRef.current);
    Object.values(canvasElsRef.current).forEach((canvas) => {
      if (canvas && canvas.dataset.pdfDoc !== token) clearCanvas(canvas);
    });
  }, []);
  const clearAllPageContent = useCallback(() => {
    Object.values(canvasElsRef.current).forEach((canvas) => { if (canvas) clearCanvas(canvas); });
    Object.values(textLayerTasksRef.current).forEach((t) => { try { t.cancel(); } catch (e) {} });
    textLayerTasksRef.current = {};
    Object.values(textLayerElsRef.current).forEach((el) => {
      if (!el) return;
      el.replaceChildren();
      delete el.dataset.pdfDoc;
    });
  }, []);

  // Tekstlaag in wereld-units (viewport scale 1): de world-transform schaalt
  // 'm mee, dus één keer tekenen per pagina is genoeg, ook bij zoomen.
  const renderTextLayer = useCallback(async (index) => {
    const doc = pdfDocRef.current;
    const el = textLayerElsRef.current[index];
    const token = String(docTokenRef.current);
    if (!ENABLE_TEXT_LAYER || !doc || !el || el.dataset.pdfDoc === token) return;
    el.dataset.pdfDoc = token;
    try { textLayerTasksRef.current[index]?.cancel(); } catch (e) {}
    el.replaceChildren();
    try {
      const page = await doc.getPage(index + 1);
      if (pdfDocRef.current !== doc || el.dataset.pdfDoc !== token) return;
      // Eerst in een los element opbouwen en pas na controle overzetten: een
      // afgebroken TextLayer verwerkt soms nog een al binnengekomen stuk
      // tekst, en zou dat anders in de (hergebruikte) laag van het VOLGENDE
      // document zetten — tekst van de vorige case over de nieuwe heen.
      const staging = document.createElement("div");
      const textLayer = new pdfjsLib.TextLayer({
        textContentSource: page.streamTextContent(),
        container: staging,
        viewport: page.getViewport({ scale: 1 }),
      });
      textLayerTasksRef.current[index] = textLayer;
      await textLayer.render();
      if (textLayerTasksRef.current[index] !== textLayer || pdfDocRef.current !== doc || el.dataset.pdfDoc !== token) return;
      // TextLayer zet zelf een width/height op basis van --total-scale-factor;
      // hier gewoon de hele pagina-box vullen. --min-font-size (door TextLayer
      // gemeten) is nodig voor de lettergrootte van de spans.
      el.style.width = "100%";
      el.style.height = "100%";
      el.style.setProperty("--min-font-size", staging.style.getPropertyValue("--min-font-size"));
      // Vangt het slepen naar lege ruimte op, zodat de selectie niet ineens
      // naar het begin/einde van de pagina springt (zie .selecting in de CSS)
      const end = document.createElement("div");
      end.className = "endOfContent";
      el.replaceChildren(...staging.childNodes, end);
    } catch (e) {
      if (e?.name !== "AbortException") console.error(e);
    }
  }, []);

  // at: optioneel de eindstand {x, y, scale} van een lopende animatie
  // (FAST_SHARPEN): dan alvast tekenen voor waar de animatie uitkomt
  const upgradeVisiblePages = useCallback((at = null) => {
    const vp = viewportElRef.current;
    const meta = pagesMetaRef.current;
    if (!vp || !meta.pages) return;
    const { x, y, scale } = at || transformRef.current;
    const margin = 500;
    const dpr = renderDpr();
    const desired = clamp(scale * dpr, 0.4, MAX_RENDER_SCALE);
    // Wat er echt in de canvas komt: verschilt de scherm-dpr (venster naar
    // ander scherm), dan ook opnieuw tekenen (SAFARI_PIXEL_EXACT)
    const effective = desired * pixelExactFactor();
    const draws = [];
    meta.pages.forEach((p, i) => {
      const sx1 = p.left * scale + x, sy1 = p.top * scale + y;
      const sx2 = sx1 + p.width * scale, sy2 = sy1 + p.height * scale;
      const visible = sx2 > -margin && sx1 < vp.clientWidth + margin && sy2 > -margin && sy1 < vp.clientHeight + margin;
      if (!visible) return;
      renderTextLayer(i);
      // Ook opnieuw tekenen na uitzoomen: een veel te grote canvas verkleinen
      // geeft rafelige letters. Na het tot rust komen precies op de huidige
      // zoom, zodat canvas- en schermpixels 1-op-1 vallen.
      const rendered = renderedScaleRef.current[i] || 0;
      if (rendered === 0 || Math.abs(effective / rendered - 1) > 0.01) {
        renderedScaleRef.current[i] = effective;
        draws.push(renderPageToCanvas(i, desired));
      }
    });
    if (!at) pendingDrawsRef.current = draws;
    alignCanvases();
  }, [renderPageToCanvas, alignCanvases, renderTextLayer]);

  // delay: na een animatie met bekende eindstand (FAST_SHARPEN) hoeft er
  // niet gewacht te worden op een volgende beweging
  const scheduleSettle = useCallback((delay = 160) => {
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      if (worldElRef.current) worldElRef.current.style.willChange = "auto";
      setUi((u) => ({ ...u, scale: transformRef.current.scale, pageIndex: computeCurrentPage() }));
      upgradeVisiblePages();
    }, delay);
  }, [computeCurrentPage, upgradeVisiblePages]);

  // SAFARI_PIXEL_EXACT: venster naar een scherm met een andere dpr (bv. van
  // extern 1x naar Retina): direct opnieuw tekenen op de nieuwe resolutie
  useEffect(() => {
    if (!SAFARI_PIXEL_EXACT || !IS_SAFARI || typeof window.matchMedia !== "function") return undefined;
    let mq = null;
    const onChange = () => { listen(); upgradeVisiblePages(); };
    const listen = () => {
      mq?.removeEventListener?.("change", onChange);
      mq = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      mq.addEventListener?.("change", onChange);
    };
    listen();
    return () => mq?.removeEventListener?.("change", onChange);
  }, [upgradeVisiblePages]);

  const animateTo = useCallback((targetX, targetY, targetScale, duration = 280, easeFn = null) => {
    if (momentumRafRef.current) { cancelAnimationFrame(momentumRafRef.current); momentumRafRef.current = null; }
    if (animRafRef.current) { cancelAnimationFrame(animRafRef.current); animRafRef.current = null; }
    animTargetRef.current = null;
    if (duration === 0) {
      // Direct toepassen, geen easing: gebruikt voor de allereerste fit na
      // het laden van een document, zodat er niets tussen "onjuist gepositio-
      // neerd" en "goed gepositioneerd" te zien is vóór de content fade-in.
      transformRef.current = { x: targetX, y: targetY, scale: targetScale };
      applyTransform();
      setUi((u) => ({ ...u, scale: targetScale, pageIndex: computeCurrentPage() }));
      upgradeVisiblePages();
      return;
    }
    const start = { ...transformRef.current };
    const startTime = performance.now();
    const ease = easeFn || ((t) => 1 - Math.pow(1 - t, 3));
    function step(now) {
      const p = Math.min((now - startTime) / duration, 1);
      const e = ease(p);
      transformRef.current = {
        x: start.x + (targetX - start.x) * e,
        y: start.y + (targetY - start.y) * e,
        scale: start.scale + (targetScale - start.scale) * e,
      };
      applyTransform();
      if (p < 1) animRafRef.current = requestAnimationFrame(step);
      else { animRafRef.current = null; animTargetRef.current = null; scheduleSettle(FAST_SHARPEN ? 0 : undefined); }
    }
    animTargetRef.current = { x: targetX, y: targetY, scale: targetScale };
    animRafRef.current = requestAnimationFrame(step);
    // De eindstand is bekend: de scherpe versie alvast tekenen, zodat hij er
    // (bijna) staat zodra de animatie klaar is
    if (FAST_SHARPEN && Math.abs(targetScale / start.scale - 1) > 0.01) {
      upgradeVisiblePages({ x: targetX, y: targetY, scale: targetScale });
    }
  }, [applyTransform, scheduleSettle, computeCurrentPage, upgradeVisiblePages]);

  const fitWidth = useCallback((instant = false) => {
    const vp = viewportElRef.current; const meta = pagesMetaRef.current;
    if (!vp || !meta.pages) return;
    const newScale = clamp((vp.clientWidth - PAD * 2) / meta.maxWidth, MIN_SCALE, MAX_SCALE);
    const cur = computeCurrentPage();
    const p = meta.pages[cur] || meta.pages[0];
    const target = focusOn(meta.maxWidth / 2, p.top, vp.clientWidth / 2, PAD, newScale);
    animateTo(target.x, target.y, newScale, instant ? 0 : undefined);
    setFitMode("width");
  }, [computeCurrentPage, animateTo, setFitMode]);

  const fitPage = useCallback((instant = false) => {
    const vp = viewportElRef.current; const meta = pagesMetaRef.current;
    if (!vp || !meta.pages) return;
    const cur = computeCurrentPage();
    const p = meta.pages[cur];
    const newScale = clamp(Math.min((vp.clientWidth - PAD * 2) / p.width, (vp.clientHeight - PAD * 2) / p.height), MIN_SCALE, MAX_SCALE);
    // Boven vastzetten op PAD i.p.v. verticaal centreren — anders staat de
    // pagina bij het laden (waar dit als eerste draait) met een grote,
    // wisselende leegte erboven, terwijl elke handmatige pan/scroll 'm
    // vanzelf dichter naar boven trekt. Zelfde ankerpunt als fitWidth.
    const target = focusOn(p.left + p.width / 2, p.top, vp.clientWidth / 2, PAD, newScale);
    animateTo(target.x, target.y, newScale, instant ? 0 : undefined);
    setFitMode("page");
  }, [computeCurrentPage, animateTo, setFitMode]);

  // Past een MEEGEGEVEN scale toe (i.p.v. 'm te berekenen zoals fitWidth/
  // fitPage doen) - gebruikt om een eerder handmatig gekozen zoomniveau
  // (geen Width/Page fit) direct na het laden van een NIEUW document te
  // herstellen (bv. bij next/previous case). Zelfde top-anker als fitPage/
  // fitWidth/resetZoom, voor dezelfde consistente afstand tot de toolbar.
  const applyCustomScale = useCallback((scale, instant = false) => {
    const vp = viewportElRef.current; const meta = pagesMetaRef.current;
    if (!vp || !meta.pages) return;
    const cur = computeCurrentPage();
    const p = meta.pages[cur];
    const clampedScale = clamp(scale, MIN_SCALE, MAX_SCALE);
    const target = focusOn(p.left + p.width / 2, p.top, vp.clientWidth / 2, PAD, clampedScale);
    animateTo(target.x, target.y, clampedScale, instant ? 0 : undefined);
    setFitMode(null);
  }, [computeCurrentPage, animateTo, setFitMode]);

  const zoomBy = useCallback((factor) => {
    const vp = viewportElRef.current; if (!vp) return;
    const t = transformRef.current;
    const cx = vp.clientWidth / 2, cy = vp.clientHeight / 2;
    const wx = (cx - t.x) / t.scale, wy = (cy - t.y) / t.scale;
    const newScale = clamp(t.scale * factor, MIN_SCALE, MAX_SCALE);
    const f = focusOn(wx, wy, cx, cy, newScale);
    const target = constrainZoom(f.x, f.y, newScale);
    animateTo(target.x, target.y, newScale, 180);
    setFitMode(null);
  }, [animateTo, setFitMode, constrainZoom]);

  const resetZoom = useCallback(() => {
    const vp = viewportElRef.current; const meta = pagesMetaRef.current;
    if (!vp || !meta.pages) return;
    // Zelfde top-anker als fitWidth/fitPage (i.p.v. zoomen rond het huidige
    // beeldcentrum) — anders landt "100%" telkens op een andere afstand tot
    // de FileCategorySelector, afhankelijk van waar je toevallig stond.
    const cur = computeCurrentPage();
    const p = meta.pages[cur];
    const target = focusOn(p.left + p.width / 2, p.top, vp.clientWidth / 2, PAD, 1);
    animateTo(target.x, target.y, 1, 220);
    setFitMode(null);
  }, [computeCurrentPage, animateTo, setFitMode]);

  const goToPage = useCallback((idx) => {
    const meta = pagesMetaRef.current; const vp = viewportElRef.current;
    if (!meta.pages || !vp || idx < 0 || idx >= meta.pages.length) return;
    const p = meta.pages[idx];
    const t = transformRef.current;
    const target = focusOn(p.left + p.width / 2, p.top, vp.clientWidth / 2, PAD, t.scale);
    animateTo(target.x, target.y, t.scale);
  }, [animateTo]);

  // Rubberband bij de rand (PDF_EDGE_BOUNCE): via de losse css-property
  // 'translate', die bovenop de pan/zoom-'transform' komt en die dus niet
  // raakt. ax/ay: { x0, v0 } per as, zie edgeSpringKeyframes. Niet opnieuw
  // zolang er nog één loopt (pijltje ingedrukt houden), behalve force
  // (loslaten na slepen: die moet altijd terug naar 0).
  const springOverscroll = useCallback((ax, ay, delay = 0, force = false) => {
    const el = worldElRef.current;
    if (!PDF_EDGE_BOUNCE || !el?.animate) return;
    if (bounceAnimRef.current && !force) return;
    bounceAnimRef.current?.cancel();
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { el.style.translate = ""; return; }
    const { keyframes, duration } = edgeSpringKeyframes(ax, ay);
    el.style.translate = "";
    const anim = el.animate(keyframes, { duration, delay });
    bounceAnimRef.current = anim;
    const done = () => { if (bounceAnimRef.current === anim) bounceAnimRef.current = null; };
    anim.onfinish = done;
    anim.oncancel = done;
  }, []);

  // Pijltjes: dir +1 = onderkant (inhoud schiet iets verder omhoog), -1 = top;
  // v0 = snelheid (px/ms) waarmee de rand geraakt wordt
  const edgeBounce = useCallback((dir, v0, delay = 0) => {
    if (v0 > 0) springOverscroll(null, { x0: 0, v0: -dir * v0 }, delay);
  }, [springOverscroll]);

  // verticaal scrollen met een fractie van de viewporthoogte (pijltjes ↑/↓)
  const scrollByViewport = useCallback((fraction) => {
    const vp = viewportElRef.current; if (!vp) return;
    const base = animTargetRef.current || transformRef.current;
    const wantY = base.y - vp.clientHeight * fraction;
    const c = clampPan(base.x, wantY, base.scale);
    // Rand geraakt: al op de rand -> een duwtje; anders met snelheid tegen
    // de rand aan scrollen en precies bij aankomst laten doorschieten
    const hitEdge = PDF_EDGE_BOUNCE && Math.abs(c.y - wantY) > 0.5;
    if (c.y === base.y) { if (hitEdge) edgeBounce(Math.sign(fraction), EDGE_NUDGE_SPEED); return; }
    if (!hitEdge) { animateTo(c.x, c.y, base.scale, 180); return; }
    const ms = 180;
    const v0 = (0.4 * Math.abs(c.y - transformRef.current.y)) / ms; // eindsnelheid van easeIntoEdge
    animateTo(c.x, c.y, base.scale, ms, easeIntoEdge);
    edgeBounce(Math.sign(fraction), Math.min(v0, 2), ms);
  }, [animateTo, edgeBounce]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- document laden zodra url/cacheKey wijzigt ---
  useEffect(() => {
    if (!url) {
      setLoadState("idle"); setContentReady(false);
      // Ander document dan het laatst geladen (bv. nieuwe case zonder pdf):
      // de oude inhoud niet in de (onzichtbare) DOM laten staan. Het oude
      // document zelf blijft geladen voor als je terugkomt.
      if (cacheKey !== loadedForKeyRef.current) clearAllPageContent();
      return;
    }
    // BELANGRIJK: hier ook `url` vergelijken, niet alleen `cacheKey`. Een
    // aanroeper kan een cacheKey meegeven die niet écht uniek is (bv. een
    // misc-bestands-id gebaseerd op array-index binnen de huidige case i.p.v.
    // een globaal uniek id) - wisselt de gebruiker dan van case terwijl beide
    // cases toevallig hetzelfde index-gebaseerde cacheKey opleveren, dan zou
    // deze hook zonder de url-check denken "dit heb ik al geladen" en het
    // PDF van de VORIGE case blijven tonen terwijl de url allang naar een
    // ander bestand wijst. Dat gaf een echt bestand-van-de-verkeerde-case-bug
    // (potentieel de verkeerde order/patiënt te zien krijgen) - vandaar deze
    // extra, onafhankelijke veiligheidscheck naast een correcte cacheKey.
    if (loadedForKeyRef.current === cacheKey && loadedForUrlRef.current === url && pdfDocRef.current) {
      // Terug naar een document dat al geladen was (bv. heen-en-weer wisselen
      // tussen tabs, of de drawer sluiten en dezelfde case weer openen):
      // status stond op "idle" terwijl deze tab/drawer niet actief was (zie
      // hierboven) — gewoon terugzetten naar "ready" triggert de eerste-fit-
      // effect hieronder (instant fit + contentReady), zonder opnieuw te
      // downloaden. Zonder dit bleef de spinner voorgoed staan.
      //
      // renderedScaleRef WEL resetten: als de drawer intussen dicht is
      // geweest (MUI ModalProps keepMounted=false) zijn de <canvas>-DOM-
      // elementen zelf afgebroken en straks bij het heropenen gloednieuw en
      // dus leeg — maar deze ref onthoudt nog de resolutie waarop de vórige
      // (inmiddels weggegooide) canvassen getekend waren. upgradeVisiblePages
      // slaat het tekenen dan over ("staat al op voldoende resolutie"),
      // waardoor de nieuwe, lege canvassen nooit gevuld worden: een
      // permanent wit vlak. Door hier te resetten wordt er altijd opnieuw
      // getekend zodra de fit-effect hieronder draait.
      renderedScaleRef.current = new Array(pagesMetaRef.current.pages?.length || 0).fill(0);
      setLoadState("loaded");
      return;
    }
    let cancelled = false;
    loadedForKeyRef.current = cacheKey;
    loadedForUrlRef.current = url;
    setLoadState("loading"); setErrorMsg(""); setContentReady(false);
    Object.values(renderTasksRef.current).forEach((t) => { try { t.cancel(); } catch (e) {} });
    renderTasksRef.current = {};
    canvasElsRef.current = {};
    Object.values(textLayerTasksRef.current).forEach((t) => { try { t.cancel(); } catch (e) {} });
    textLayerTasksRef.current = {};
    docTokenRef.current += 1;
    // Tekst van het vorige document meteen weg (is transparant, dus geen
    // visueel verschil); de canvassen blijven nog even staan voor de fade-out
    // en worden vlak vóór het tonen opgeruimd (clearStaleCanvases)
    Object.values(textLayerElsRef.current).forEach((el) => {
      if (!el) return;
      el.replaceChildren();
      delete el.dataset.pdfDoc;
    });
    transformRef.current = { x: 0, y: 0, scale: 1 };
    // Het vorige document destroyen vóórdat de ref wordt overschreven — anders
    // blijft de worker-kant van pdf.js (gedecodeerde pagina's, image-caches)
    // van elk eerder geopend PDF in het geheugen hangen. Dit gebeurt NIET in
    // de cleanup hieronder, want die draait ook bij de "cache hit"-heropening
    // van hetzelfde document (zie boven) — daar moet pdfDocRef juist intact
    // blijven.
    if (pdfDocRef.current) {
      const prevDoc = pdfDocRef.current;
      pdfDocRef.current = null;
      // .destroy() zit op de PDFDocumentLoadingTask, niet op de resolved
      // PDFDocumentProxy zelf — die is bereikbaar via .loadingTask.
      prevDoc.loadingTask.destroy().catch(() => {});
    }
    // Zelfde token-header als apiClient.js gebruikt voor de rest van de app —
    // /files/proxy zit achter dezelfde verifyToken-middleware.
    const token = localStorage.getItem("token");
    const httpHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;
    // CASE_PREFETCH: alvast geladen bij de vorige/volgende case? Sleutel is
    // exact deze url, dus dit is altijd precies dit document. Anders gewoon
    // via de url, en zolang dat loopt pauzeert de prefetch van de buren.
    const prefetched = CASE_PREFETCH ? getPrefetchedPdf(url) : null;
    let endForeground = null;
    let prefetchedUsed = false;
    const openDoc = (data) => {
      if (cancelled) return null;
      prefetchedUsed = !!data;
      if (data) return pdfjsLib.getDocument({ data }).promise;
      if (CASE_PREFETCH) endForeground = beginForeground();
      return pdfjsLib.getDocument({ url, httpHeaders }).promise;
    };
    (prefetched ? prefetched.then(openDoc) : Promise.resolve(openDoc(null))).then(async (doc) => {
      if (!doc) return;
      if (cancelled) { doc.loadingTask.destroy().catch(() => {}); return; }
      pdfDocRef.current = doc;
      const pages = []; let top = 0, maxWidth = 0;
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const vp = page.getViewport({ scale: 1 });
        pages.push({ width: vp.width, height: vp.height, top, left: 0 });
        top += vp.height + PAGE_GAP;
        if (vp.width > maxWidth) maxWidth = vp.width;
      }
      if (cancelled) { pdfDocRef.current = null; doc.loadingTask.destroy().catch(() => {}); return; }
      // CASE_PREFETCH: via de url geladen? Dan de bytes ook in de cache, voor
      // als je terugbladert (getData: pas als alles binnen is, kost niks extra)
      if (CASE_PREFETCH && !prefetchedUsed) doc.getData().then((bytes) => storePdf(url, bytes)).catch(() => {});
      pages.forEach((p) => { p.left = (maxWidth - p.width) / 2; });
      pagesMetaRef.current = { pages, maxWidth };
      renderedScaleRef.current = new Array(doc.numPages).fill(0);
      // Bewust NIET scale/pageIndex/fitMode hier al resetten (bv. naar 1/0/
      // null): dat zou héél even (vóórdat de "ready"-fit hieronder de ECHTE
      // waarde berekent) een verkeerde tussenstand laten zien in de toolbar -
      // bv. altijd kort terug naar "100%" bij elke case-wissel, met een
      // zichtbare breedte-hik in de controls tot gevolg. Alleen numPages
      // moet al kloppen (voor de pagina-teller); scale/pageIndex/fitMode
      // volgen in exact dezelfde stap als de instant-fit hieronder, dus
      // springt de toolbar in één keer naar de juiste eindstand.
      setUi((u) => ({ ...u, numPages: doc.numPages }));
      setLoadState("loaded");
    }).catch((e) => {
      if (cancelled) return;
      console.error(e);
      setLoadState("error"); setErrorMsg(e?.message || "Kon PDF niet laden");
    }).finally(() => endForeground?.());
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, cacheKey, retryToken]);

  // Mislukt (bv. tijdens alvast laden op de achtergrond): bij het actief
  // worden opnieuw proberen. Zonder url-wissel draait het laden anders niet
  // meer, en bleef de foutmelding staan tot een andere case.
  useEffect(() => {
    if (active && loadState === "error") setRetryToken((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // destroy het geladen document zodra deze hook-instantie écht unmount —
  // CaseDetailDrawer zelf blijft altijd gemount, maar deze hook is generiek
  // herbruikbaar; zonder dit zou een losstaand gebruik van
  // usePdfCanvasViewer het laatst geladen document nooit opruimen.
  useEffect(() => {
    return () => {
      if (pdfDocRef.current) {
        pdfDocRef.current.loadingTask.destroy().catch(() => {});
        pdfDocRef.current = null;
      }
    };
  }, []);

  // Niet actief = niet getoond; bij het weer actief worden draait de fit
  // hieronder opnieuw en zet die contentReady pas weer als alles klaarstaat
  useEffect(() => {
    if (!active) setContentReady(false);
  }, [active]);

  // eerste fit zodra 'ready' (Page als default) — instant (geen animatie),
  // en pas dán contentReady zetten, zodat de fade-in nooit een spring toont.
  useEffect(() => {
    if (status !== "ready") return;
    // Alles opnieuw laten tekenen: na een tijdje niet actief (alvast geladen
    // document, of de drawer is dicht geweest) kunnen de canvassen leeg zijn
    // terwijl renderedScaleRef nog een resolutie onthoudt. Bij een vers
    // geladen document staat alles al op 0.
    renderedScaleRef.current = new Array(pagesMetaRef.current.pages?.length || 0).fill(0);
    let cancelled = false;
    let rafId = null;
    let timerId = null;
    let attempt = 0;
    // Bij een vers geopende case-drawer loopt de open-transitie (MUI Slide)
    // nog terwijl deze fit draait: de viewport kan dan een onbetrouwbare
    // (bv. nul) maat teruggeven, waardoor de fit een kapotte schaal/positie
    // berekent en geen enkele pagina binnen beeld valt — er wordt dan nooit
    // iets op canvas getekend (blijvend wit vlak). Bij next/previous case
    // blijft de drawer gewoon open (geen transitie), dus daar gaat het altijd
    // goed. In plaats van te gokken hoe lang de transitie duurt: na elke fit-
    // poging checken of er ECHT een pagina als zichtbaar is herkend
    // (renderedScaleRef > 0 betekent 'm hebben we voor render aangeboden) —
    // zo niet, na een oplopende delay opnieuw proberen (viewport-maat kan
    // dan al wel kloppen), tot het lukt of we het na een paar pogingen
    // opgeven. contentReady (en dus de fade-in) pas zetten zodra het echt
    // gelukt is, zodat de spinner blijft staan i.p.v. een wit vlak.
    const delays = [60, 150, 300, 600, 1000];
    const tryFit = () => {
      if (cancelled) return;
      if (!defaultFit && typeof initialScale === "number") {
        // Geen Width/Page fit onthouden, maar wel een eerder handmatig
        // gekozen zoomniveau (bv. via +/-, scrollen met ctrl, of dubbelklik)
        // - dat herstellen i.p.v. terugvallen op de Page-fit.
        applyCustomScale(initialScale, true);
      } else if (defaultFit === "width") {
        fitWidth(true);
      } else {
        fitPage(true);
      }
      const rendered = renderedScaleRef.current.some((s) => s > 0);
      if (rendered && WAIT_FOR_FIRST_DRAW) {
        // Wachten tot de zichtbare pagina's getekend zijn (renderPageToCanvas
        // vangt zelf fouten af, dus dit resolvet altijd); de timeout is een
        // vangnet zodat de viewer nooit onzichtbaar blijft.
        const done = () => { if (!cancelled) { clearStaleCanvases(); setContentReady(true); } };
        Promise.all(pendingDrawsRef.current).then(done);
        timerId = setTimeout(done, FIRST_DRAW_TIMEOUT_MS);
        return;
      }
      if (rendered || attempt >= delays.length) {
        clearStaleCanvases();
        setContentReady(true);
        return;
      }
      timerId = setTimeout(tryFit, delays[attempt]);
      attempt += 1;
    };
    rafId = requestAnimationFrame(tryFit);
    return () => { cancelled = true; cancelAnimationFrame(rafId); clearTimeout(timerId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // wheel: native listener i.v.m. preventDefault (React's onWheel is passive)
  useEffect(() => {
    if (status !== "ready") return;
    const vp = viewportElRef.current; if (!vp) return;
    function onWheel(e) {
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      const cx = e.clientX - rect.left, cy = e.clientY - rect.top;
      if (e.ctrlKey || e.metaKey) {
        const t = transformRef.current;
        const wx = (cx - t.x) / t.scale, wy = (cy - t.y) / t.scale;
        const factor = Math.exp(-e.deltaY * 0.012);
        const newScale = clamp(t.scale * factor, MIN_SCALE, MAX_SCALE);
        const f = focusOn(wx, wy, cx, cy, newScale);
        const nf = constrainZoom(f.x, f.y, newScale);
        transformRef.current = { x: nf.x, y: nf.y, scale: newScale };
        setFitMode(null);
      } else {
        const raw = { x: transformRef.current.x - e.deltaX, y: transformRef.current.y - e.deltaY };
        const c = clampPan(raw.x, raw.y, transformRef.current.scale);
        transformRef.current = { ...transformRef.current, x: c.x, y: c.y };
      }
      queueApply(); scheduleSettle();
    }
    vp.addEventListener("wheel", onWheel, { passive: false });
    return () => vp.removeEventListener("wheel", onWheel);
  }, [status, queueApply, scheduleSettle, setFitMode, constrainZoom]);

  // resize: actieve fit-modus opnieuw toepassen, of anders clampen
  useEffect(() => {
    if (status !== "ready") return;
    const vp = viewportElRef.current; if (!vp) return;
    let timer = null;
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (fitModeRef.current === "width") { fitWidth(); return; }
        if (fitModeRef.current === "page") { fitPage(); return; }
        const t = transformRef.current;
        const c = clampPan(t.x, t.y, t.scale);
        if (c.x !== t.x || c.y !== t.y) { transformRef.current = { ...t, x: c.x, y: c.y }; applyTransform(); }
      }, 120);
    });
    ro.observe(vp);
    return () => { ro.disconnect(); clearTimeout(timer); };
  }, [status, fitWidth, fitPage, applyTransform]);

  // NATIVE_TOUCH_SCROLL: de browser scrolt; positie terugrekenen naar de
  // transform, en onderweg al de pagina's tekenen die in beeld komen (het
  // scrollen zelf loopt daar niet op vast)
  useEffect(() => {
    if (!NATIVE_TOUCH_SCROLL || status !== "ready") return undefined;
    const vp = viewportElRef.current; if (!vp) return undefined;
    let lastUpgrade = 0;
    function onScroll() {
      const o = nativeOriginRef.current;
      transformRef.current = { ...transformRef.current, x: o.ox - vp.scrollLeft, y: o.oy - vp.scrollTop };
      if (pinchRef.current || animRafRef.current) return;
      const now = performance.now();
      if (now - lastUpgrade > 250) { lastUpgrade = now; upgradeVisiblePages(); }
      scheduleSettle();
    }
    vp.addEventListener("scroll", onScroll, { passive: true });
    return () => vp.removeEventListener("scroll", onScroll);
  }, [status, upgradeVisiblePages, scheduleSettle]);

  // iOS Safari: pinch via de eigen gesture-events (scale + middelpunt). Die
  // zijn daar betrouwbaarder dan twee losse pointers, die Safari kan
  // afbreken. Alleen tijdens een touch: een pinch op het trackpad van een Mac
  // stuurt ook gesture-events, en die blijft gaan zoals hij ging.
  useEffect(() => {
    if (status !== "ready" || typeof window === "undefined" || !("ongesturestart" in window)) return undefined;
    const vp = viewportElRef.current; if (!vp) return undefined;
    const anchorAt = (e) => {
      const rect = vp.getBoundingClientRect();
      return { cx: e.clientX - rect.left, cy: e.clientY - rect.top };
    };
    // Touchscherm (niet het trackpad van een Mac). Niet wachten op een al
    // geregistreerde vinger: iOS kan gesturestart vóór de pointerdowns sturen.
    const isTouchScreen = window.matchMedia?.("(pointer: coarse)").matches;
    function onStart(e) {
      if (!isTouchScreen && touchPtsRef.current.size === 0) return;
      e.preventDefault();
      if (worldElRef.current) worldElRef.current.style.translate = "";
      dragRef.current = null;
      // Native scroll even uit tijdens de pinch (anders scrollen de vingers mee)
      if (NATIVE_TOUCH_SCROLL) vp.style.overflow = "hidden";
      const t = transformRef.current;
      const { cx, cy } = anchorAt(e);
      pinchRef.current = { gesture: true, s0: t.scale, wx: (cx - t.x) / t.scale, wy: (cy - t.y) / t.scale };
    }
    function onChange(e) {
      const pinch = pinchRef.current;
      if (!pinch?.gesture) return;
      e.preventDefault();
      const { cx, cy } = anchorAt(e);
      const newScale = clamp(pinch.s0 * e.scale, MIN_SCALE, MAX_SCALE);
      const f = focusOn(pinch.wx, pinch.wy, cx, cy, newScale);
      const c = constrainZoom(f.x, f.y, newScale);
      transformRef.current = { x: c.x, y: c.y, scale: newScale };
      if (fitModeRef.current !== null) setFitMode(null);
      queueApply();
    }
    function onEnd(e) {
      if (!pinchRef.current?.gesture) return;
      e.preventDefault();
      lastTouchAtRef.current = performance.now();
      finishPinchRef.current?.();
      // Nog een vinger op het scherm die nog events stuurt: daarmee verder pannen
      const rest = [...touchPtsRef.current.values()][0];
      if (rest) startTouchPan(rest.x, rest.y);
    }
    vp.addEventListener("gesturestart", onStart, { passive: false });
    vp.addEventListener("gesturechange", onChange, { passive: false });
    vp.addEventListener("gestureend", onEnd, { passive: false });
    return () => {
      vp.removeEventListener("gesturestart", onStart);
      vp.removeEventListener("gesturechange", onChange);
      vp.removeEventListener("gestureend", onEnd);
    };
  }, [status, queueApply, setFitMode, constrainZoom]);

  // keyboard shortcuts (alleen actief zolang deze viewer 'ready' is)
  useEffect(() => {
    function onKeyDown(e) {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return;
      if (e.code === "Space" && !spaceHeldRef.current) {
        spaceHeldRef.current = true;
        if (viewportElRef.current && !dragRef.current) viewportElRef.current.style.cursor = "grab";
        viewportElRef.current?.classList.add("pdf-panning");
        e.preventDefault();
      }
      if (status !== "ready") return;
      if (e.key === "+" || e.key === "=") { zoomBy(1.25); e.preventDefault(); }
      else if (e.key === "-") { zoomBy(1 / 1.25); e.preventDefault(); }
      else if (e.key === "0") { resetZoom(); e.preventDefault(); }
      else if (arrowKeys && e.key === "ArrowDown") { scrollByViewport(0.25); e.preventDefault(); }
      else if (arrowKeys && e.key === "ArrowUp") { scrollByViewport(-0.25); e.preventDefault(); }
      else if (e.key === "PageDown") { goToPage(Math.min(ui.pageIndex + 1, ui.numPages - 1)); e.preventDefault(); }
      else if (e.key === "PageUp") { goToPage(Math.max(ui.pageIndex - 1, 0)); e.preventDefault(); }
    }
    function onKeyUp(e) {
      if (e.code === "Space") {
        spaceHeldRef.current = false;
        if (viewportElRef.current && !dragRef.current) {
          viewportElRef.current.style.cursor = "default";
          viewportElRef.current.classList.remove("pdf-panning");
        }
      }
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => { window.removeEventListener("keydown", onKeyDown); window.removeEventListener("keyup", onKeyUp); };
  }, [status, ui.pageIndex, ui.numPages, zoomBy, resetZoom, goToPage, scrollByViewport, arrowKeys]);

  // decay per frame; touch gebruikt een lagere rem (zoals iOS zelf scrollt)
  function startMomentum(vx, vy, decay = 0.92) {
    if (momentumRafRef.current) cancelAnimationFrame(momentumRafRef.current);
    function step() {
      vx *= decay; vy *= decay;
      if (Math.abs(vx) < 0.5 && Math.abs(vy) < 0.5) { momentumRafRef.current = null; scheduleSettle(); return; }
      const raw = { x: transformRef.current.x + vx, y: transformRef.current.y + vy };
      const c = clampPan(raw.x, raw.y, transformRef.current.scale);
      // Rand geraakt met snelheid: doorschieten en terugveren (v per frame
      // -> px/ms). Een as die bevroren is (inhoud past) telt niet.
      const hitX = c.x !== raw.x && c.x !== transformRef.current.x ? vx / 16.7 : 0;
      const hitY = c.y !== raw.y && c.y !== transformRef.current.y ? vy / 16.7 : 0;
      if (hitX || hitY) springOverscroll(hitX ? { x0: 0, v0: hitX } : null, hitY ? { x0: 0, v0: hitY } : null);
      if (c.x !== raw.x) vx = 0;
      if (c.y !== raw.y) vy = 0;
      transformRef.current = { ...transformRef.current, x: c.x, y: c.y };
      applyTransform();
      momentumRafRef.current = requestAnimationFrame(step);
    }
    momentumRafRef.current = requestAnimationFrame(step);
  }

  // Past de huidige pagina niet meer in z'n geheel in de viewport (verder
  // ingezoomd dan Fit page)? 1px marge tegen afrondingsverschillen.
  function pageOverflows() {
    const vp = viewportElRef.current; const meta = pagesMetaRef.current;
    if (!vp || !meta.pages || !meta.pages.length) return false;
    const p = meta.pages[computeCurrentPage()];
    const s = transformRef.current.scale;
    return p.width * s > vp.clientWidth - PAD * 2 + 1 || p.height * s > vp.clientHeight - PAD * 2 + 1;
  }
  const isOnText = (e) => ENABLE_TEXT_LAYER && !!e.target.closest?.("[data-pdf-text-layer] span");

  // Touch: één vinger pant altijd (er is geen scrollwiel), twee vingers
  // pinchen rond hun midden, dubbeltik = inzoomen op die plek of terug naar
  // Fit page. Muis en pen gaan verder precies zoals hieronder.
  const touchPtsRef = useRef(new Map()); // pointerId -> { x, y, sx, sy, t }
  const pinchRef = useRef(null);
  const lastTapRef = useRef(null);
  const lastTouchAtRef = useRef(0);
  // Actuele finishPinch voor de (één keer ingestelde) gesture-listeners
  const finishPinchRef = useRef(null);
  function fitPageScale() {
    const vp = viewportElRef.current; const meta = pagesMetaRef.current;
    if (!vp || !meta.pages || !meta.pages.length) return null;
    const p = meta.pages[computeCurrentPage()];
    return Math.min((vp.clientWidth - PAD * 2) / p.width, (vp.clientHeight - PAD * 2) / p.height);
  }
  function startTouchPan(x, y) {
    if (NATIVE_TOUCH_SCROLL) return; // de browser scrolt zelf
    const t = transformRef.current;
    dragRef.current = { startClientX: x, startClientY: y, origX: t.x, origY: t.y, lastX: x, lastY: y, lastT: performance.now(), vx: 0, vy: 0 };
  }
  function onTouchDown(e) {
    if (!NATIVE_TOUCH_SCROLL) {
      e.preventDefault();
      // Kan een NotFoundError geven (pointer al weg); mag de rest niet stoppen,
      // anders telt deze vinger niet mee en komt er nooit een pinch
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }
    touchPtsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, t: performance.now() });
    if (momentumRafRef.current) { cancelAnimationFrame(momentumRafRef.current); momentumRafRef.current = null; }
    if (bounceAnimRef.current) { bounceAnimRef.current.cancel(); }
    const pts = [...touchPtsRef.current.values()];
    if (pts.length >= 2) {
      // Tweede vinger: van pannen naar pinchen
      if (worldElRef.current) worldElRef.current.style.translate = "";
      if (NATIVE_TOUCH_SCROLL) viewportElRef.current.style.overflow = "hidden";
      dragRef.current = null;
      const [a, b] = pts;
      const rect = viewportElRef.current.getBoundingClientRect();
      const t = transformRef.current;
      const cx = (a.x + b.x) / 2 - rect.left, cy = (a.y + b.y) / 2 - rect.top;
      // Safari's gesture-events (hieronder) kunnen al begonnen zijn
      if (!pinchRef.current) pinchRef.current = { d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, s0: t.scale, wx: (cx - t.x) / t.scale, wy: (cy - t.y) / t.scale };
      return;
    }
    if (NATIVE_TOUCH_SCROLL) return;
    e.currentTarget.classList.add("pdf-panning");
    startTouchPan(e.clientX, e.clientY);
  }
  function onPinchMove() {
    const pinch = pinchRef.current;
    const [a, b] = [...touchPtsRef.current.values()];
    if (!pinch || pinch.gesture || !b) return;
    const rect = viewportElRef.current.getBoundingClientRect();
    const cx = (a.x + b.x) / 2 - rect.left, cy = (a.y + b.y) / 2 - rect.top;
    const newScale = clamp(pinch.s0 * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.d0), MIN_SCALE, MAX_SCALE);
    // Het punt tussen de vingers blijft onder de vingers (dus pinchen en
    // tegelijk schuiven kan)
    const f = focusOn(pinch.wx, pinch.wy, cx, cy, newScale);
    const c = constrainZoom(f.x, f.y, newScale);
    transformRef.current = { x: c.x, y: c.y, scale: newScale };
    if (fitModeRef.current !== null) setFitMode(null);
    queueApply();
  }
  // Pinch klaar: kleiner dan Fit page = terug naar Fit page, anders scherp
  // tekenen op de nieuwe zoom
  function finishPinch() {
    pinchRef.current = null;
    if (NATIVE_TOUCH_SCROLL && viewportElRef.current) viewportElRef.current.style.overflow = "";
    lastTapRef.current = null;
    const fit = fitPageScale();
    if (fit && transformRef.current.scale < fit) fitPage();
    else scheduleSettle();
  }
  finishPinchRef.current = finishPinch;
  function onTouchUp(e) {
    const pt = touchPtsRef.current.get(e.pointerId);
    if (!pt) return;
    touchPtsRef.current.delete(e.pointerId);
    const now = performance.now();
    lastTouchAtRef.current = now;
    // iOS breekt de vingers af (pointercancel) zodra het een pinch als eigen
    // gesture ziet; daarna komen er voor die vingers geen events meer. Dan
    // geen tik/veeg/momentum: alleen het slepen netjes stoppen. De pinch zelf
    // loopt verder via de gesture-events en wordt in gestureend afgerond.
    if (e.type === "pointercancel") {
      lastTapRef.current = null;
      if (touchPtsRef.current.size === 0) {
        dragRef.current = null;
        if (worldElRef.current) worldElRef.current.style.translate = "";
        viewportElRef.current?.classList.remove("pdf-panning");
        if (pinchRef.current && !pinchRef.current.gesture) finishPinch();
      }
      return;
    }
    if (pinchRef.current) {
      if (touchPtsRef.current.size >= 2) return;
      // Een gesture-pinch rondt gestureend af
      if (pinchRef.current.gesture) return;
      finishPinch();
      // Eén vinger blijft staan: daarmee verder pannen
      const rest = [...touchPtsRef.current.values()][0];
      if (rest) startTouchPan(rest.x, rest.y);
      return;
    }
    if (touchPtsRef.current.size > 0) return;
    const isTap = now - pt.t < 250 && Math.hypot(e.clientX - pt.sx, e.clientY - pt.sy) < 10;
    if (!isTap) {
      lastTapRef.current = null;
      const d = dragRef.current;
      if (d) {
        // Veegsnelheid over de laatste ~100 ms; vinger al even stil = geen vaart
        const smp = d.samples || [];
        const first = smp[0], last = smp[smp.length - 1];
        if (first && last && last.t > first.t && now - last.t < 60) {
          d.vx = (last.x - first.x) / (last.t - first.t);
          d.vy = (last.y - first.y) / (last.t - first.t);
        } else { d.vx = 0; d.vy = 0; }
        d.touch = true;
      }
      onPointerUp();
      return;
    }
    dragRef.current = null;
    viewportElRef.current?.classList.remove("pdf-panning");
    const prev = lastTapRef.current;
    if (!prev || now - prev.t > 300 || Math.hypot(e.clientX - prev.x, e.clientY - prev.y) > 30) {
      lastTapRef.current = { t: now, x: e.clientX, y: e.clientY };
      return;
    }
    lastTapRef.current = null;
    const fit = fitPageScale();
    const t = transformRef.current;
    if (fitModeRef.current === "page" || (fit && t.scale <= fit * 1.01)) {
      const rect = viewportElRef.current.getBoundingClientRect();
      const cx = e.clientX - rect.left, cy = e.clientY - rect.top;
      const newScale = clamp(t.scale * 2.5, MIN_SCALE, MAX_SCALE);
      const f = focusOn((cx - t.x) / t.scale, (cy - t.y) / t.scale, cx, cy, newScale);
      const c = constrainZoom(f.x, f.y, newScale);
      animateTo(c.x, c.y, newScale, 260);
      setFitMode(null);
    } else {
      fitPage();
    }
  }

  function onPointerDown(e) {
    if (e.pointerType === "touch") { onTouchDown(e); return; }
    const isMiddle = e.button === 1;
    const dragPan = DRAG_PAN_WHEN_ZOOMED && e.button === 0 && !isOnText(e) && pageOverflows();
    if (!(spaceHeldRef.current || isMiddle || dragPan)) {
      // Selecteren in de tekstlaag: die laag op "selecting" zetten tot de
      // muis weer los is (zie .endOfContent in PdfCanvasViewer)
      const layer = ENABLE_TEXT_LAYER && e.button === 0 && e.target.closest?.("[data-pdf-text-layer]");
      if (layer) {
        layer.classList.add("selecting");
        const done = () => { layer.classList.remove("selecting"); window.removeEventListener("pointerup", done); window.removeEventListener("pointercancel", done); };
        window.addEventListener("pointerup", done);
        window.addEventListener("pointercancel", done);
      }
      return;
    }
    e.preventDefault();
    e.currentTarget.classList.add("pdf-panning");
    if (momentumRafRef.current) { cancelAnimationFrame(momentumRafRef.current); momentumRafRef.current = null; }
    // Lopende rubberband stoppen; slepen bepaalt nu zelf de uitwijking
    if (bounceAnimRef.current) { bounceAnimRef.current.cancel(); }
    const t = transformRef.current;
    dragRef.current = { startClientX: e.clientX, startClientY: e.clientY, origX: t.x, origY: t.y, lastX: e.clientX, lastY: e.clientY, lastT: performance.now(), vx: 0, vy: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.style.cursor = "grabbing";
  }
  function onPointerMove(e) {
    if (e.pointerType === "touch") {
      const pt = touchPtsRef.current.get(e.pointerId);
      if (pt) { pt.x = e.clientX; pt.y = e.clientY; }
      if (pinchRef.current) { onPinchMove(); return; }
    }
    const d = dragRef.current; if (!d) return;
    const dx = e.clientX - d.startClientX, dy = e.clientY - d.startClientY;
    const raw = { x: d.origX + dx, y: d.origY + dy };
    const c = clampPan(raw.x, raw.y, transformRef.current.scale);
    transformRef.current = { ...transformRef.current, x: c.x, y: c.y };
    // Voorbij de rand: de inhoud gaat (steeds stroever) mee als uitwijking
    // bovenop de geklemde positie; bij loslaten veert die terug
    const vp = viewportElRef.current;
    if (PDF_EDGE_BOUNCE && vp && worldElRef.current) {
      d.overX = rubberAxes(transformRef.current.scale).x ? rubber(raw.x - c.x, vp.clientWidth) : 0;
      d.overY = rubber(raw.y - c.y, vp.clientHeight);
      worldElRef.current.style.translate = d.overX || d.overY ? `${d.overX}px ${d.overY}px` : "";
    }
    const now = performance.now(); const dt = Math.max(now - d.lastT, 1);
    d.vx = (e.clientX - d.lastX) / dt; d.vy = (e.clientY - d.lastY) / dt;
    // Touch: posities van de laatste ~100 ms bewaren; de snelheid uit één
    // event is op iOS te grillig (samengevoegde events) voor een goede veeg
    if (e.pointerType === "touch") {
      (d.samples ||= []).push({ x: e.clientX, y: e.clientY, t: now });
      while (d.samples.length > 2 && now - d.samples[0].t > 100) d.samples.shift();
    }
    d.lastX = e.clientX; d.lastY = e.clientY; d.lastT = now;
    queueApply();
  }
  function onPointerUp(e) {
    if (e?.pointerType === "touch") { onTouchUp(e); return; }
    const d = dragRef.current; dragRef.current = null;
    if (viewportElRef.current) {
      viewportElRef.current.style.cursor = spaceHeldRef.current ? "grab" : "default";
      if (!spaceHeldRef.current) viewportElRef.current.classList.remove("pdf-panning");
    }
    scheduleSettle();
    if (!d) return;
    // Losgelaten voorbij de rand: terugveren, en in die richting geen
    // momentum meer
    const overX = d.overX || 0, overY = d.overY || 0;
    if (overX || overY) springOverscroll(overX ? { x0: overX, v0: 0 } : null, overY ? { x0: overY, v0: 0 } : null, 0, true);
    const vx = overX ? 0 : d.vx, vy = overY ? 0 : d.vy;
    if (Math.abs(vx) > 0.05 || Math.abs(vy) > 0.05) startMomentum(vx * 16, vy * 16, d.touch ? 0.965 : 0.92);
  }
  function onDoubleClick(e) {
    // Een dubbeltik is al afgehandeld in onTouchUp
    if (lastTouchAtRef.current && performance.now() - lastTouchAtRef.current < 600) return;
    if (DOUBLE_CLICK_FIT_PAGE) {
      // Op tekst: de browser selecteert het woord
      if (isOnText(e)) return;
      fitPage();
      return;
    }
    const vp = viewportElRef.current; if (!vp) return;
    const rect = vp.getBoundingClientRect();
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top;
    const t = transformRef.current;
    const wx = (cx - t.x) / t.scale, wy = (cy - t.y) / t.scale;
    const dir = e.altKey ? 1 / 1.6 : 1.6;
    const newScale = clamp(t.scale * dir, MIN_SCALE, MAX_SCALE);
    const target = focusOn(wx, wy, cx, cy, newScale);
    animateTo(target.x, target.y, newScale, 260);
    setFitMode(null);
  }

  // Opent direct de printdialoog met het al geladen document (geen nieuwe
  // download). Via een onzichtbare iframe; Safari print pdf's in een iframe
  // onbetrouwbaar (leeg of de hele app), daar openen we 'm in een nieuw
  // tabblad. Dat venster moet synchroon open, anders blokkeert de popup-blocker.
  function print() {
    // Alleen het document dat nu echt in beeld staat (pdfDocRef kan na een
    // case-wissel zonder pdf nog het vorige document bevatten, voor de cache)
    const doc = pdfDocRef.current; if (!doc || !visibleReady || loadState !== "loaded") return;
    const isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent);
    const win = isSafari ? window.open("", "_blank") : null;
    doc.getData().then((data) => {
      const blobUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      if (win) {
        win.location.href = blobUrl;
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        return;
      }
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
      iframe.src = blobUrl;
      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        iframe.remove();
        URL.revokeObjectURL(blobUrl);
      };
      // Vangnet: als onload nooit komt (download i.p.v. tonen, laadfout)
      // blijven iframe + pdf anders voorgoed in het geheugen hangen.
      const fallback = setTimeout(cleanup, 120000);
      iframe.onload = () => {
        clearTimeout(fallback);
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          window.open(blobUrl, "_blank");
        }
        // Pas na een tijdje opruimen: print() blokkeert niet in elke browser
        setTimeout(cleanup, 60000);
      };
      document.body.appendChild(iframe);
    }).catch((err) => {
      console.error("PDF print error:", err);
      if (win) win.close();
    });
  }

  // Alleen "klaar" voor het document dat nu gevraagd wordt: in de render
  // waarin url/cacheKey net veranderd is (vóórdat het laad-effect draait)
  // staat contentReady nog op true voor het vorige document.
  const isRequestedDoc = loadedForKeyRef.current === cacheKey && loadedForUrlRef.current === url;
  const visibleReady = active && contentReady && isRequestedDoc;

  return {
    status, errorMsg, contentReady: visibleReady,
    // Document voor deze url klaar met laden (of mislukt), ook als de tab niet
    // actief is — voor CASE_PREFETCH (pas daarna de buur-cases laden)
    loadSettled: isRequestedDoc && (loadState === "loaded" || loadState === "error"),
    ui, // { scale, pageIndex, numPages, fitMode }
    pages: pagesMetaRef.current.pages,
    viewportRef: viewportElRef,
    worldRef: worldElRef,
    sizerRef: sizerElRef,
    registerCanvas: (index) => (el) => { if (el) canvasElsRef.current[index] = el; },
    registerTextLayer: (index) => (el) => { if (el) textLayerElsRef.current[index] = el; },
    // Met tekstlaag is dubbelklik "woord selecteren" (native), niet zoomen
    containerHandlers: {
      onPointerDown, onPointerMove, onPointerUp, onPointerLeave: onPointerUp,
      // Alleen touch (iOS breekt vingers af bij een pinch); muis/pen zoals voorheen
      onPointerCancel: (e) => { if (e.pointerType === "touch") onPointerUp(e); },
      ...((!ENABLE_TEXT_LAYER || DOUBLE_CLICK_FIT_PAGE) && { onDoubleClick }),
    },
    actions: { goToPage, zoomIn: () => zoomBy(1.25), zoomOut: () => zoomBy(1 / 1.25), resetZoom, fitWidth, fitPage, print },
  };
}
